import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Heading,
  Trash2,
  ArrowUp,
  ArrowDown,
  Image as ImageIcon,
  Pencil,
  SlidersHorizontal,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { FONT_LABELS, type SlideElement, type ThemeFont } from '@/lib/types';

interface ElementPropertiesProps {
  element: SlideElement;
  onChange: (updates: Partial<SlideElement>) => void;
  onDelete: () => void;
  onLayerChange: (direction: 'up' | 'down') => void;
  onPickImage: () => void;
  /** Open the full text-content editor dialog (text elements) */
  onEditText: () => void;
}

/**
 * Compact, horizontal element editor designed to live in a bottom strip
 * (so it doesn't eat horizontal canvas space). Common controls are inline;
 * position/size/rotation/opacity live in a "Layout" popover.
 */
export function ElementProperties({
  element,
  onChange,
  onDelete,
  onLayerChange,
  onPickImage,
  onEditText,
}: ElementPropertiesProps) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-sm font-semibold capitalize w-14 shrink-0">{element.type}</span>

      {/* Text controls */}
      {element.type === 'text' && (
        <>
          <Button variant="outline" size="sm" onClick={onEditText}>
            <Pencil className="w-4 h-4 mr-1.5" />
            Edit text
          </Button>

          <div className="flex items-end gap-1">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Size</Label>
              <Input
                type="number"
                className="h-8 w-16"
                min={8}
                max={200}
                value={element.fontSize ?? 32}
                onChange={(e) => onChange({ fontSize: parseInt(e.target.value) || 32 })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Font</Label>
              <Select
                value={element.fontFamily ?? '__theme'}
                onValueChange={(v) =>
                  onChange({ fontFamily: v === '__theme' ? undefined : (v as ThemeFont) })
                }
              >
                <SelectTrigger className="h-8 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__theme">Theme font</SelectItem>
                  {(Object.keys(FONT_LABELS) as ThemeFont[]).map((f) => (
                    <SelectItem key={f} value={f}>{FONT_LABELS[f]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Color</Label>
              <input
                type="color"
                className="h-8 w-9 rounded border cursor-pointer bg-transparent block"
                value={element.color ?? '#ffffff'}
                onChange={(e) => onChange({ color: e.target.value })}
                title={element.color ? 'Override color' : 'Theme color (click to override)'}
              />
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              variant={element.align === 'left' || !element.align ? 'secondary' : 'ghost'}
              size="icon" className="h-8 w-8"
              onClick={() => onChange({ align: 'left' })}
            >
              <AlignLeft className="w-4 h-4" />
            </Button>
            <Button
              variant={element.align === 'center' ? 'secondary' : 'ghost'}
              size="icon" className="h-8 w-8"
              onClick={() => onChange({ align: 'center' })}
            >
              <AlignCenter className="w-4 h-4" />
            </Button>
            <Button
              variant={element.align === 'right' ? 'secondary' : 'ghost'}
              size="icon" className="h-8 w-8"
              onClick={() => onChange({ align: 'right' })}
            >
              <AlignRight className="w-4 h-4" />
            </Button>
            <Button
              variant={element.bold ? 'secondary' : 'ghost'}
              size="icon" className="h-8 w-8"
              onClick={() => onChange({ bold: !element.bold })}
            >
              <Bold className="w-4 h-4" />
            </Button>
            <Button
              variant={element.heading ? 'secondary' : 'ghost'}
              size="icon" className="h-8 w-8"
              title="Use theme heading color"
              onClick={() => onChange({ heading: !element.heading })}
            >
              <Heading className="w-4 h-4" />
            </Button>
          </div>
        </>
      )}

      {/* Image controls */}
      {element.type === 'image' && (
        <>
          <Button variant="outline" size="sm" onClick={onPickImage}>
            <ImageIcon className="w-4 h-4 mr-1.5" />
            Replace
          </Button>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Fit</Label>
            <Select
              value={element.fit ?? 'contain'}
              onValueChange={(fit: 'contain' | 'cover' | 'fill') => onChange({ fit })}
            >
              <SelectTrigger className="h-8 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contain">Contain</SelectItem>
                <SelectItem value="cover">Cover</SelectItem>
                <SelectItem value="fill">Stretch</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {/* Shape controls */}
      {element.type === 'shape' && (
        <>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Shape</Label>
            <Select
              value={element.shape ?? 'rect'}
              onValueChange={(shape: 'rect' | 'ellipse' | 'line') => onChange({ shape })}
            >
              <SelectTrigger className="h-8 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rect">Rectangle</SelectItem>
                <SelectItem value="ellipse">Ellipse</SelectItem>
                <SelectItem value="line">Line</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Fill</Label>
            <input
              type="color"
              className="h-8 w-9 rounded border cursor-pointer bg-transparent block"
              value={element.fill ?? '#3b82f6'}
              onChange={(e) => onChange({ fill: e.target.value })}
            />
          </div>
        </>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Layout popover (position/size/rotation/opacity) */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm">
            <SlidersHorizontal className="w-4 h-4 mr-1.5" />
            Layout
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="end" className="w-64 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">X</Label>
              <Input type="number" className="h-8" value={Math.round(element.x)}
                onChange={(e) => onChange({ x: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Y</Label>
              <Input type="number" className="h-8" value={Math.round(element.y)}
                onChange={(e) => onChange({ y: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Width</Label>
              <Input type="number" className="h-8" value={Math.round(element.width)}
                onChange={(e) => onChange({ width: Math.max(24, parseInt(e.target.value) || 24) })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Height</Label>
              <Input type="number" className="h-8" value={Math.round(element.height)}
                onChange={(e) => onChange({ height: Math.max(24, parseInt(e.target.value) || 24) })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Rotation: {element.rotation ?? 0}°</Label>
            <Slider min={-180} max={180} step={1} value={[element.rotation ?? 0]}
              onValueChange={([v]) => onChange({ rotation: v })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Opacity: {Math.round((element.opacity ?? 1) * 100)}%</Label>
            <Slider min={0} max={100} step={5} value={[(element.opacity ?? 1) * 100]}
              onValueChange={([v]) => onChange({ opacity: v / 100 })} />
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-8" />

      {/* Layering + delete */}
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Bring forward"
        onClick={() => onLayerChange('up')}>
        <ArrowUp className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Send backward"
        onClick={() => onLayerChange('down')}>
        <ArrowDown className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete"
        onClick={onDelete}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}
